import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { Form, Formik } from "formik";
import gql from "graphql-tag";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { toastSuccessOptions } from "../../../App";
import inviteSSOUser from "../../../GraphQL/Mutation/inviteSSOUser";
import { handleGraphQLErrors } from "../../../utils/handleGraphQLError";
import { Button, ButtonLoading } from "../../SharedComponents/Button/Button";
import {
  EditableInformation,
  EditableInformationItemWrapper,
  EditableInformationTextInput,
  EditableInformationTextSelect,
} from "../../SharedComponents/EditableInformation/EditableInformation";
import { EditHeader } from "../../SharedComponents/EditHeader/EditHeader";
import "./AdminUsersNew.css";

export const AdminUsersNew = () => {
  const { t } = useTranslation();
  const history = useHistory();
  /** @type {*} */
  const params = useParams();
  const [selectOptions, setSelectOptions] = useState([]);
  const [creatingUser, setCreatingUser] = useState(false);

  const { data } = useQuery(allAccountsQuery);
  const [invite] = useMutation(inviteSSOUser);
  const [getUser, { data: userData }] = useLazyQuery(getUserQuery);

  const handleInviteUser = ({ accountId, email, name }) => {
    setCreatingUser(true);
    invite({ variables: { accountId, email, name } })
      .then(() => {
        setCreatingUser(false);
        toast(`${email} ${t("invited")}`, toastSuccessOptions);
        history.goBack();
      })
      .catch((err) => {
        setCreatingUser(false);
        handleGraphQLErrors(err);
      });
  };

  useEffect(() => {
    if (data?.accounts) {
      setSelectOptions(
        data?.accounts?.map((acc) => ({ value: acc.id, label: acc.name }))
      );
    }
  }, [data]);

  return (
    <div className="AdminDistributorEdit">
      <Formik
        initialValues={{
          email: "",
          name: "",
          accountId: params.id || "",
        }}
        onSubmit={handleInviteUser}
        validationSchema={validationSchema}
      >
        {({ isValid, values, setValues, dirty }) => {
          if (
            userData?.user?.name &&
            values.name === "" &&
            values.email !== ""
          ) {
            setValues({ ...values, name: userData.user.name });
          }
          return (
            <Form style={{ display: "flex", width: "100%" }}>
              <div className="AdminDistributorEdit__content">
                <EditHeader
                  type="types.user"
                  name={t("users.inviteUser")}
                  status={null}
                  goBack={() => history.goBack()}
                />
                <div className="AdminDistributorEdit__companyinfo">
                  <EditableInformation title="User information">
                    <>
                      <EditableInformationItemWrapper label="E-mail">
                        <EditableInformationTextInput
                          name="email"
                          type="text"
                          defaultEditingState={true}
                          onBlur={() => {
                            if (values.email !== "") {
                              getUser({
                                variables: { email: values.email },
                              });
                            } else {
                              setValues({ ...values, name: "" });
                            }
                          }}
                        />
                      </EditableInformationItemWrapper>
                      <EditableInformationItemWrapper label="Name">
                        <EditableInformationTextInput
                          name="name"
                          type="text"
                          defaultEditingState={true}
                          disabled={userData?.user?.name ? true : false}
                        />
                      </EditableInformationItemWrapper>
                      {!params.id && (
                        <EditableInformationItemWrapper label="Account">
                          <EditableInformationTextSelect
                            defaultEditingState={true}
                            name="accountId"
                            type="select"
                            options={selectOptions}
                          />
                        </EditableInformationItemWrapper>
                      )}
                    </>
                  </EditableInformation>
                </div>
              </div>
              <div className="AdminDistributorEdit__right">
                <div className="AdminDistributorEdit__right--actions">
                  <Button
                    buttonStyle="Button__primary"
                    disabled={!isValid || !dirty}
                    type="submit"
                  >
                    {creatingUser ? <ButtonLoading /> : t("users.inviteUser")}
                  </Button>
                  <Button
                    buttonStyle="Button__secondary"
                    click={(e) => {
                      e.preventDefault();
                      history.goBack();
                    }}
                  >
                    {t("generic.cancel")}
                  </Button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

const allAccountsQuery = gql`
  query {
    accounts {
      id
      name
    }
  }
`;

const validationSchema = Yup.object().shape({
  email: Yup.string().email().required("errors.mustEnterEmail"),
  name: Yup.string(),
});

const getUserQuery = gql`
  query user($email: String) {
    user(email: $email) {
      id
      name
    }
  }
`;
